"use client";

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Button } from "@/components/ui/button";
import { X, Check, ChevronUp, ChevronDown } from "lucide-react";
import { useMediaQuery } from "@/hooks/use-media-query";

export interface TourStepInfo {
  id: string;
  title: string;
  text: string;
  progressLabel?: string;
  isFirst?: boolean;
  isLast?: boolean;
  /** Module context (when in modular onboarding) */
  moduleTitle?: string;
  subStepIndex?: number;
  totalSubSteps?: number;
  completedSubSteps?: string[];
  subStepTitles?: string[];
}

interface TourStepModalProps {
  open: boolean;
  step: TourStepInfo | null;
  onBack: () => void;
  onNext: () => void;
  onSkip: () => void;
}

export function TourStepModal({
  open,
  step,
  onBack,
  onNext,
  onSkip,
}: TourStepModalProps) {
  useEffect(() => {
    if (open && step) {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open, step]);

  const { isMobile } = useMediaQuery();
  const [collapsed, setCollapsed] = useState(true);

  useEffect(() => {
    if (!isMobile) setCollapsed(false);
  }, [isMobile]);

  if (!step || !open) return null;

  const isModule = Boolean(step.moduleTitle && step.totalSubSteps != null);
  const progressLabel = step.progressLabel ?? (isModule && step.subStepIndex != null
    ? `${step.moduleTitle} - Step ${step.subStepIndex + 1} of ${step.totalSubSteps}`
    : undefined);

  const card = (
    <div
      className="fixed z-[10002] w-full max-w-md sm:bottom-6 sm:right-6 sm:left-auto left-0 right-0 bottom-0 sm:bottom-4 sm:rounded-lg rounded-t-lg overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-200"
      role="dialog"
      aria-labelledby="tour-step-title"
      aria-modal="true"
    >
      <div className="w-full max-w-lg mx-auto">
        <div className="h-1 bg-gradient-to-r from-transparent via-zinc-300 dark:via-zinc-600 to-transparent" />
        <div className="rounded-t-lg sm:rounded-b-lg border border-t-0 border-border/50 bg-background shadow-lg relative">
          {/* Mobile: collapse/expand bar with skip X on the LEFT */}
          {isMobile && (
            <div className="flex items-center gap-2 px-3 py-2.5 border-b border-border/50">
              <button
                type="button"
                onClick={onSkip}
                className="rounded-sm opacity-70 hover:opacity-100 p-1 shrink-0"
                aria-label={isModule ? "Skip module" : "Close tour"}
              >
                <X className="size-4" />
              </button>
              <button
                type="button"
                onClick={() => setCollapsed((c) => !c)}
                className="flex-1 flex items-center justify-between gap-2 text-left"
                aria-expanded={!collapsed}
              >
                <span className="text-sm font-medium truncate">{progressLabel ?? step.title}</span>
                {collapsed ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronUp className="h-4 w-4 shrink-0" />}
              </button>
            </div>
          )}
          {(!isMobile || !collapsed) && (
          <>
          {/* Desktop: X in the top-right corner */}
          {!isMobile && (
            <button
              type="button"
              onClick={onSkip}
              className="absolute right-3 top-3 rounded-sm opacity-70 hover:opacity-100 p-1 focus:outline-none focus:ring-2 focus:ring-ring"
              aria-label={isModule ? "Skip module" : "Close tour"}
            >
              <X className="size-4" />
            </button>
          )}
          <div className={isMobile ? "px-3 py-2 space-y-2" : "p-5 pr-10 space-y-3"}>
            {progressLabel && !isMobile && (
              <p className="text-xs font-medium text-muted-foreground">
                {progressLabel}
              </p>
            )}
            {isModule && step.subStepTitles && step.subStepTitles.length > 0 && (() => {
              const curIdx = step.subStepIndex ?? 0;
              const total = step.subStepTitles.length;
              // On mobile: show a window of up to 3 items around the current step in 2 columns
              // On desktop: show all items in a single column
              const visibleItems = isMobile
                ? step.subStepTitles.map((label, i) => ({ label, i })).filter(({ i }) => {
                    const start = Math.max(0, Math.min(curIdx - 1, total - 3));
                    const end = start + 3;
                    return i >= start && i < end;
                  })
                : step.subStepTitles.map((label, i) => ({ label, i }));
              const showEllipsisBefore = isMobile && visibleItems.length > 0 && visibleItems[0].i > 0;
              const showEllipsisAfter = isMobile && visibleItems.length > 0 && visibleItems[visibleItems.length - 1].i < total - 1;
              return (
                <>
                  {showEllipsisBefore && <span className="text-[10px] text-muted-foreground/50 pl-1">&hellip;</span>}
                  <ul className={isMobile ? "grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]" : "space-y-1.5 text-xs"}>
                    {visibleItems.map(({ label, i }) => {
                      const done = (step.completedSubSteps?.length ?? 0) > i;
                      const current = step.subStepIndex === i;
                      return (
                        <li
                          key={i}
                          className={`flex items-center gap-1.5 ${isMobile ? "min-w-0" : "gap-2"} ${current ? "font-medium text-foreground" : done ? "text-muted-foreground" : "text-muted-foreground/70"}`}
                        >
                          {i < (step.completedSubSteps?.length ?? 0) ? (
                            <span className="flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full bg-green-500/20 text-green-600 dark:text-green-400">
                              <Check className="h-2 w-2" />
                            </span>
                          ) : (
                            <span className={`flex shrink-0 items-center justify-center rounded-full border border-border text-[10px] tabular-nums ${isMobile ? "h-3.5 w-3.5" : "h-4 w-4"}`}>
                              {i + 1}
                            </span>
                          )}
                          <span className="truncate">{label}</span>
                        </li>
                      );
                    })}
                  </ul>
                  {showEllipsisAfter && <span className="text-[10px] text-muted-foreground/50 pl-1">&hellip;</span>}
                </>
              );
            })()}
            <h2 id="tour-step-title" className={isMobile ? "text-sm font-semibold tracking-tight" : "text-lg font-semibold tracking-tight pr-6"}>
              {step.title}
            </h2>
            {/* Explanation text - desktop only */}
            {!isMobile && (
              <div
                className="text-sm text-muted-foreground prose prose-sm max-w-none dark:prose-invert"
                dangerouslySetInnerHTML={{ __html: step.text }}
              />
            )}
          </div>
          <div className={`flex flex-row gap-2 bg-muted/30 border-t ${isMobile ? "px-3 pb-3 pt-2" : "px-5 pb-5 pt-3"}`}>
            {!step.isFirst && (
              <Button
                variant="outline"
                size="sm"
                className="flex-1"
                onClick={onBack}
              >
                Back
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              className={step.isFirst ? "flex-1" : ""}
              onClick={onSkip}
            >
              {isModule ? "Skip" : "Skip Tour"}
            </Button>
            <Button
              size="sm"
              className="flex-1 bg-foreground text-background hover:bg-foreground/90"
              onClick={onNext}
            >
              {step.isLast && isModule ? "Complete" : step.isLast ? "Finish" : "Next"}
            </Button>
          </div>
          </>
          )}
        </div>
      </div>
    </div>
  );

  return typeof document !== "undefined" ? createPortal(card, document.body) : null;
}
